import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, InputNumber, Spin, Empty, Switch, Form, DatePicker, Table, Alert } from "antd";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { apiLentes, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import EntidadFotoCard from "./EntidadFotoCard";
import ActividadTab from "./ActividadTab";

import "./PaquetesPage.css";
import "./EventoDetallePage.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

const fmtFechaCorta = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

function stockTag(stock, manejaInventario) {
  if (!manejaInventario) return { nivel: "none", text: "Sin control de inventario" };
  if (stock === undefined || stock === null) return { nivel: "none", text: "Sin datos" };
  if (stock <= 0) return { nivel: "empty", text: "Sin existencias" };
  if (stock < 5) return { nivel: "low", text: "Stock bajo" };
  return { nivel: "ok", text: "Stock saludable" };
}

export default function LenteDetallePage() {
  const { idLente } = useParams();
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [lente, setLente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("datos");

  const [editingInfo, setEditingInfo] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  const [editForm, setEditForm] = useState({});

  const [movimientos, setMovimientos] = useState([]);
  const [loadingMov, setLoadingMov] = useState(false);

  const [movModal, setMovModal] = useState({ open: false, tipo: null });
  const [movForm] = Form.useForm();
  const [savingMov, setSavingMov] = useState(false);
  const [movError, setMovError] = useState("");

  const fetchLente = useCallback(() => {
    setLoading(true);
    apiLentes
      .get(`/lentes/${idLente}`, { headers: authHeader() })
      .then((res) => setLente(res.data))
      .catch(() => toast.error("No se pudo cargar el lente"))
      .finally(() => setLoading(false));
  }, [idLente]);

  const fetchMovimientos = useCallback(() => {
    setLoadingMov(true);
    apiLentes
      .get(`/lentes/${idLente}/movimientos`, { headers: authHeader() })
      .then((res) => setMovimientos(Array.isArray(res.data) ? res.data : res.data?.items ?? []))
      .catch(() => toast.error("No se pudo cargar el kardex de movimientos"))
      .finally(() => setLoadingMov(false));
  }, [idLente]);

  useEffect(() => { fetchLente(); }, [fetchLente]);

  useEffect(() => {
    if (activeTab !== "movimientos") return;
    fetchMovimientos();
  }, [activeTab, fetchMovimientos]);

  const startEditInfo = () => {
    setEditForm({
      nombre_modelo: lente.nombre_modelo,
      poder: lente.poder,
      proveedor: lente.proveedor,
      maneja_inventario: Boolean(lente.maneja_inventario),
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    if (!editForm.nombre_modelo?.trim()) return toast.warning("El nombre del modelo es obligatorio");
    setSavingInfo(true);
    try {
      await apiLentes.put(`/lentes/${idLente}`, editForm, { headers: authHeader() });
      toast.success("Lente actualizado correctamente");
      setEditingInfo(false);
      fetchLente();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar el lente");
    } finally {
      setSavingInfo(false);
    }
  };

  const handleFoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await apiLentes.post(`/lentes/${idLente}/foto`, fd, { headers: authHeader() });
    if (res.data?.item) setLente(res.data.item);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: "¿Eliminar este lente?",
      content: "Esta accion desactiva el modelo del catalogo. No se puede deshacer.",
      okText: "Eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        setDeleting(true);
        try {
          await apiLentes.delete(`/lentes/${idLente}`, { headers: authHeader() });
          toast.success("Lente eliminado");
          navigate("/app/lentes");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "No se pudo eliminar el lente");
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const openMovModal = (tipo) => {
    movForm.resetFields();
    setMovError("");
    setMovModal({ open: true, tipo });
  };

  const closeMovModal = () => {
    setMovModal({ open: false, tipo: null });
    setMovError("");
  };

  const handleSaveMov = async () => {
    try {
      const values = await movForm.validateFields();
      setMovError("");

      if (movModal.tipo === "salida" && lente?.maneja_inventario && lente?.stock_actual != null) {
        if (values.cantidad > lente.stock_actual) {
          setMovError(
            `No puedes registrar una salida de ${values.cantidad} unidades: el stock actual es de solo ${lente.stock_actual}.`
          );
          return;
        }
      }

      setSavingMov(true);
      const payload = {
        tipo: movModal.tipo,
        cantidad: values.cantidad,
        lote: values.lote || undefined,
        fecha: values.fecha ? values.fecha.toISOString() : undefined,
      };
      await apiLentes.post(`/lentes/${idLente}/movimientos`, payload, { headers: authHeader() });
      toast.success(movModal.tipo === "entrada" ? "Entrada registrada correctamente" : "Salida registrada correctamente");
      closeMovModal();
      fetchLente();
      fetchMovimientos();
    } catch (err) {
      if (err?.errorFields) return;
      const backendMsg = err?.response?.data?.detail;
      if (err?.response?.status === 400 && backendMsg) {
        setMovError(typeof backendMsg === "string" ? backendMsg : "El movimiento no es valido: revisa la cantidad ingresada.");
      } else {
        toast.error(backendMsg || "No se pudo registrar el movimiento");
      }
    } finally {
      setSavingMov(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!lente) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Lente no encontrado" />
      </div>
    );
  }

  const tag = stockTag(lente.stock_actual, Boolean(lente.maneja_inventario));

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <h1 className="cd-client-name">{(lente.nombre_modelo || "—").toUpperCase()}</h1>
              <div className="cd-header-meta">
                {lente.proveedor && <span className="cd-meta-item"><i className="fa-solid fa-truck-field" /> {lente.proveedor}</span>}
                <span className="cd-meta-item">
                  <i className="fa-solid fa-circle" style={{ fontSize: 8, color: lente.active ? "#15803d" : "#b45309" }} />
                  {lente.active ? "Activo" : "Inactivo"}
                </span>
              </div>
            </div>
            <div className="cd-header-actions">
              {perm("lentes", "eliminar") && (
                <Button icon={<i className="fa-solid fa-trash" />} className="cd-btn-delete" loading={deleting} onClick={handleDelete}>
                Eliminar
              </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "movimientos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("movimientos")}>
              <i className="fa-solid fa-clock-rotate-left" /> Movimientos (kardex)
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-info-layout">
            <EntidadFotoCard item={lente} icon="fa-glasses" canEdit={perm("lentes", "editar")} onUpload={handleFoto} />
            <div style={{ flex: 1, minWidth: 0 }}>
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-glasses"></i></div>
                <h2 className="cd-card-title">Informacion del modelo</h2>
              </div>
              {!editingInfo && perm("lentes", "editar") && (
                  <Button className="cd-btn-edit" onClick={startEditInfo}>Editar</Button>
)}
            </div>

            {editingInfo ? (
              <div>
                <div className="cd-edit-grid">
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Nombre del modelo</span>
                    <Input value={editForm.nombre_modelo} onChange={(e) => setEditForm((f) => ({ ...f, nombre_modelo: e.target.value }))} />
                  </div>
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Poder</span>
                    <Input value={editForm.poder || ""} onChange={(e) => setEditForm((f) => ({ ...f, poder: e.target.value }))} />
                  </div>
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Proveedor</span>
                    <Input value={editForm.proveedor || ""} onChange={(e) => setEditForm((f) => ({ ...f, proveedor: e.target.value }))} />
                  </div>
                  <div className="cd-edit-field">
                    <span className="cd-edit-label">Maneja inventario</span>
                    <Switch checked={editForm.maneja_inventario} onChange={(v) => setEditForm((f) => ({ ...f, maneja_inventario: v }))} />
                  </div>
                </div>
                <div className="cd-edit-actions">
                  <Button icon={<CloseOutlined />} onClick={() => setEditingInfo(false)} disabled={savingInfo}>Cancelar</Button>
                  <Button type="primary" icon={<SaveOutlined />} loading={savingInfo} onClick={handleSaveInfo}>Guardar</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 24 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                  <div><span className="cd-field-label">Nombre del modelo</span><span className="cd-field-value">{lente.nombre_modelo}</span></div>
                  <div><span className="cd-field-label">Poder</span><span className="cd-field-value">{lente.poder || "—"}</span></div>
                  <div><span className="cd-field-label">Proveedor</span><span className="cd-field-value">{lente.proveedor || "—"}</span></div>
                  <div><span className="cd-field-label">Maneja inventario</span><span className="cd-field-value">{lente.maneja_inventario ? "Si" : "No"}</span></div>
                  <div><span className="cd-field-label">Estado</span><span className="cd-field-value" style={{ color: lente.active ? "#15803d" : "#b45309" }}>{lente.active ? "Activo" : "Inactivo"}</span></div>
                </div>
                <div className={`cd-stock-card cd-stock-${tag.nivel}`}>
                  <div className="cd-stock-icon"><i className="fa-solid fa-boxes-stacked" /></div>
                  <span className="cd-stock-label">Stock actual</span>
                  <div className="cd-stock-value">{lente.maneja_inventario ? lente.stock_actual ?? "—" : "—"}</div>
                  <span className="cd-stock-unit">{lente.maneja_inventario ? "unidades disponibles" : "no se controla existencia"}</span>
                  <span className="cd-stock-tag">{tag.text}</span>
                </div>
              </div>
            )}
          </div>
            </div>
          </div>
        )}

        {activeTab === "actividad" && (
          <ActividadTab
            titulo="Actividad del modelo"
            fetcher={() => apiLentes.get(`/lentes/${idLente}/actividad`, { headers: authHeader() })}
          />
        )}

        {activeTab === "movimientos" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock-rotate-left"></i></div>
              <h2 className="cd-card-title">Kardex de movimientos</h2>
              <span style={{ marginLeft: "auto", fontWeight: 700, color: "#7a7f87" }}>Stock actual: {lente.stock_actual ?? "—"}</span>
            </div>

            <div className="cd-kardex-toolbar">
              {perm("lentes", "insertar") && (
                <Button className="cd-btn-entrada" icon={<i className="fa-solid fa-arrow-down-to-bracket" />} onClick={() => openMovModal("entrada")}>
                Registrar entrada
              </Button>
              )}
              {perm("lentes", "insertar") && (
                <Button className="cd-btn-salida" icon={<i className="fa-solid fa-arrow-up-from-bracket" />} onClick={() => openMovModal("salida")}>
                Registrar salida
              </Button>
              )}
            </div>

            {loadingMov ? (
              <Spin />
            ) : movimientos.length === 0 ? (
              <Empty description="Todavia no hay movimientos registrados para este lente" />
            ) : (
              <Table
                rowKey={(r) => r.id_movimiento || `${r.tipo}-${r.fecha}-${r.cantidad}`}
                dataSource={movimientos}
                pagination={{ pageSize: 10 }}
                columns={[
                  {
                    title: "Tipo",
                    dataIndex: "tipo",
                    render: (t) => (
                      <span
                        style={{
                          color: t === "entrada" ? "#15803d" : "#b91c1c",
                          background: t === "entrada" ? "#dcfce7" : "#fee2e2",
                          padding: "2px 10px",
                          borderRadius: 999,
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        {t === "entrada" ? "Entrada" : "Salida"}
                      </span>
                    ),
                  },
                  { title: "Cantidad", dataIndex: "cantidad" },
                  { title: "Lote", dataIndex: "lote", render: (v) => v || "—" },
                  { title: "Fecha", dataIndex: "fecha", render: (v, r) => fmtFechaCorta(v || r.datetime) },
                ]}
              />
            )}
          </div>
        )}
      </div>

      <Modal
        open={movModal.open}
        onCancel={closeMovModal}
        onOk={handleSaveMov}
        okText={movModal.tipo === "entrada" ? "Registrar entrada" : "Registrar salida"}
        cancelText="Cancelar"
        title={
          <span>
            <i
              className={movModal.tipo === "entrada" ? "fa-solid fa-arrow-down-to-bracket" : "fa-solid fa-arrow-up-from-bracket"}
              style={{ marginRight: 8, color: movModal.tipo === "entrada" ? "#15803d" : "#b91c1c" }}
            />
            {movModal.tipo === "entrada" ? "Registrar entrada de inventario" : "Registrar salida de inventario"}
          </span>
        }
        confirmLoading={savingMov}
        centered
        okButtonProps={{
          style:
            movModal.tipo === "entrada"
              ? { background: "#15803d", borderColor: "#15803d" }
              : { background: "#b91c1c", borderColor: "#b91c1c" },
        }}
      >
        {movModal.tipo === "salida" && lente?.stock_actual != null && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 14 }}
            message={`Stock disponible actualmente: ${lente.stock_actual} unidades.`}
          />
        )}
        {movError && (
          <Alert type="error" showIcon style={{ marginBottom: 14 }} message={movError} />
        )}
        <Form form={movForm} layout="vertical" onValuesChange={() => movError && setMovError("")}>
          <Form.Item
            name="cantidad"
            label={<span className="paq-field-label">Cantidad</span>}
            rules={[{ required: true, message: "La cantidad es obligatoria" }]}
          >
            <InputNumber min={1} style={{ width: "100%" }} placeholder="Ej. 5" />
          </Form.Item>
          <Form.Item name="lote" label={<span className="paq-field-label">Lote</span>}>
            <Input placeholder="Numero o clave de lote (opcional)" />
          </Form.Item>
          <Form.Item name="fecha" label={<span className="paq-field-label">Fecha</span>}>
            <DatePicker showTime style={{ width: "100%" }} placeholder="Usar fecha y hora actuales" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
